 
import React from "react";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { StandadCard } from "../../../../../../components/Cards";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { LoadState, MODULE_IDS } from "../../../../../../constants/enums";
import { useDispatchWrapper } from "../../../../../../hooks";
import { Navigate } from "react-router-dom";
import { useFormik } from "formik";
import { Grid, Stack, Typography } from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../components/formsComponents";

import {
  IBusinessPolicyCoverage,
  ICoverageRow,
} from "../../../../../../redux/businessPolicy/businessPolicyCoverage/businessPolicyCoverage.types";
import produce from "immer";
import {
  addBusinesPolicyCoverageAsync,
  clearBusinessPolicyCoverage,
  fetchBusinessPolicyCoverageAsync,
} from "../../../../../../redux/businessPolicy/businessPolicyCoverage/businesPolicyCoverageActions";
import { CoveragePropertyTable } from "./CoveragePropertyTable";
import { CoverageBusinessInterruptionTable } from "./CoverageBusinessInterruption";
import { CoverageMachineryBreakdownTable } from "./CoverageMachineryBreakdown";
import { CoverageCrimeTable } from "./CoverageCrime";
import { CoverageLiability } from "./CoverageLiability";
import { CoverageUmbrellaLiability } from "./CoverageUmbrellaLiability";
import { CoverageExcessLiability } from "./CoverageExcesssLiability";
import { CoverageOther } from "./CoverageOther";
import { getUniqueId } from "../../../../../../helpers";
import { RoleBasedCustomButton } from "../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { batch } from "react-redux";
import { fetchBusinessPolicyRiskListAsync } from "../../../../../../redux/businessPolicy/buisenessPolicyRiskLocation/businessPolicyRiskLocationActions";
import { COMMON_STATUS } from "../../../../../../constants/constants";
import { FieldSet } from "../../../../../../components/FieldSet/FieldSet";
import { PageContainer } from "../../../../../../components/container/PageContainer";

export type SectionKey =
  | "property"
  | "business_interruption"
  | "machinery_breakdown"
  | "crime"
  | "liability"
  | "umbrella_liability"
  | "excess_liability"
  | "other";

interface ICoverageProps {
  customerId?: number;
  customerPolicyId?: number;
  coverageId?: number;
  readOnly?: boolean;
  fromRightPanel?: boolean;
  staticStatusValue?: string;
  created_from?: string;
  onSaveSuccess: (riskLocation: IBusinessPolicyCoverage) => void;
  initialData?: IBusinessPolicyCoverage;
  disableApiCalls?: boolean;
  disableSaveApiCall?: boolean;
}

export const Coverage: React.FC<ICoverageProps> = (props) => {
  const {
    customerId,
    customerPolicyId,
    coverageId,
    onSaveSuccess,
    created_from = "Coverage",
    fromRightPanel = false,
    readOnly = false,
    staticStatusValue,
    initialData,
    disableApiCalls = false,
    disableSaveApiCall = false,
  } = props;

  const { coverageLoading, coverage, error } = useSelector(
    (storeState: IStoreState) => storeState.business.coverage
  );
  const dispatch = useDispatchWrapper();
  const [saveLoading, setSaveLoading] = React.useState(false);
  const riskLocations = useSelector(
    (storeState: IStoreState) => storeState.business.riskLocation.data
  );

  const { values, handleChange, handleSubmit, setFieldValue, setValues } =
    useFormik({
      initialValues: initialData ? initialData : coverage,
      validate: (values) => {},
      onSubmit: (values) => {
        if (disableSaveApiCall) {
          onSaveSuccess(values);
        } else {
          if (customerPolicyId && customerId) {
            let finalStatus = values.status;
            if (staticStatusValue) {
              finalStatus = staticStatusValue;
            }
            setSaveLoading(true);
            dispatch(
              addBusinesPolicyCoverageAsync(
                {
                  ...values,
                  customer_policy_id: customerPolicyId,
                  status: finalStatus,
                  created_from: created_from,
                },
                (isSuccess, data) => {
                  if (isSuccess && data) {
                    window.scrollTo(0, 0);
                    onSaveSuccess(data);
                  }
                  setSaveLoading(false);
                }
              )
            );
          }
        }
      },
    });

  React.useEffect(() => {
    if (customerPolicyId && coverageId && !disableApiCalls) {
      dispatch(fetchBusinessPolicyCoverageAsync(customerPolicyId, coverageId));
    }
  }, [customerPolicyId, coverageId]);

  React.useEffect(() => {
    if (initialData && disableApiCalls) {
      return;
    }
    setValues({ ...coverage, coverage_number: coverageId || null });
  }, [coverage]);

  React.useEffect(() => {
    return () => {
      dispatch(clearBusinessPolicyCoverage());
    };
  }, []);

  const handleRowChange = React.useCallback(
    (sectionKey: SectionKey, fieldKey: keyof ICoverageRow, rowIndex: number, value: string) => {
      if (values[sectionKey].length > rowIndex) {
        const finalRows = produce(values[sectionKey], (draftRows) => {
          draftRows[rowIndex][fieldKey] = value;
        });
        setFieldValue(sectionKey, finalRows);
      }
    },
    [values, setFieldValue]
  );

  const handleAddRow = React.useCallback(
    (sectionKey: SectionKey, currentRowIndex: number) => {
      const row: ICoverageRow = {
        key: getUniqueId(),
        type: "",
        co_insurance: "",
        deductible: "",
        limit: "",
        rate: "",
        premium: "",
      };
      const finalValues = [...values[sectionKey]];
      finalValues.splice(currentRowIndex + 1, 0, row);
      setFieldValue(sectionKey, finalValues);
    },
    [values, setFieldValue]
  );

  const handleRemoveRow = React.useCallback(
    (sectionKey: SectionKey, index: number) => {
      const finalRows = values[sectionKey].filter((_, i) => i !== index);
      setFieldValue(sectionKey, finalRows);
    },
    [values, setFieldValue]
  );

  React.useEffect(() => {
    if (customerPolicyId) {
      batch(() => {
        dispatch(fetchBusinessPolicyRiskListAsync(customerPolicyId, "ACTIVE"));
      });
    }
  }, []);
  const riskLocationsDropDown = React.useMemo(() => {
    return riskLocations.map((item) => {
      return {
        label:
          item.location_number +
          ` (${item.address}, ${item.city}, ${item.province_or_state}, ${item.postal_code})`,
        value: item.location_number || "",
      };
    });
  }, [riskLocations]);

  if (!customerPolicyId) {
    return <Navigate to={"/customers-list"} />;
  }

  return (
    <PageContainer title={coverageId
        ? "Edit Coverage"
            : "Create Coverage"
    } description="this is inner page">
    <PageLoader
      loading={coverageLoading === LoadState.InProgress}
      error={error ? { message: error } : undefined}
    >
      <StandadCard
        sx={{ mt: 2 }}
        renderWithoutCard={fromRightPanel}
        heading={!fromRightPanel ? "Coverage Summary" : ""}
      >
        <form onSubmit={handleSubmit}>
          <FieldSet disabled={readOnly}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Coverage Number</CustomFormLabel>
                <CustomTextField
                  name="coverage_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  disabled
                  value={values.coverage_number}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Risk Location</CustomFormLabel>

                <ControlledCustomSelect
                  variant="outlined"
                  size="small"
                  type="string"
                  name="risk_location"
                  fullWidth
                  displayEmpty
                  value={values.risk_location}
                  onChange={handleChange}
                  placeholder="Select One"
                  options={riskLocationsDropDown}
                ></ControlledCustomSelect>
              </Grid>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Status</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.status}
                  name="status"
                  onChange={handleChange}
                  placeholder="Select one"
                  displayEmpty
                  options={COMMON_STATUS}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 3 }}>PROPERTY</Typography>
            <CoveragePropertyTable
              rows={values.property}
              onAddRow={(currentRowIndex: number) => handleAddRow("property", currentRowIndex)}
              onRemoveRow={(index: number) => handleRemoveRow("property", index)}
              onRowChange={(key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("property", key, rowIndex, value)}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>BUSINESS INTERRUPTION</Typography>
            <CoverageBusinessInterruptionTable
              rows={values.business_interruption}
              onAddRow={(currentRowIndex: number) => handleAddRow("business_interruption", currentRowIndex)}
              onRemoveRow={(index: number) => handleRemoveRow("business_interruption", index)}
              onRowChange={(key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("business_interruption", key, rowIndex, value)}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>MACHINERY BREAKDOWN</Typography>
            <CoverageMachineryBreakdownTable
              rows={values.machinery_breakdown}
              onAddRow={(currentRowIndex: number) => handleAddRow("machinery_breakdown", currentRowIndex)}
              onRemoveRow={(index: number) => handleRemoveRow("machinery_breakdown", index)}
              onRowChange={(key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("machinery_breakdown", key, rowIndex, value)}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>CRIME</Typography>
            <CoverageCrimeTable
              rows={values.crime}
              onAddRow={(currentRowIndex: number) => handleAddRow("crime", currentRowIndex)}
              onRemoveRow={(index: number) => handleRemoveRow("crime", index)}
              onRowChange={(key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("crime", key, rowIndex, value)}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>LIABILITY</Typography>
            <CoverageLiability
              rows={values.liability}
              onAddRow={(currentRowIndex: number) => handleAddRow("liability", currentRowIndex)}
              onRemoveRow={(index: number) => handleRemoveRow("liability", index)}
              onRowChange={(key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("liability", key, rowIndex, value)}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>UMBRELLA LIABILITY</Typography>
            <CoverageUmbrellaLiability
              rows={values.umbrella_liability}
              onAddRow={(currentRowIndex: number) => handleAddRow("umbrella_liability", currentRowIndex)}
              onRemoveRow={(index: number) => handleRemoveRow("umbrella_liability", index)}
              onRowChange={(key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("umbrella_liability", key, rowIndex, value)}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>EXCESS LIABILITY</Typography>
            <CoverageExcessLiability
              rows={values.excess_liability}
              onAddRow={(currentRowIndex: number) => handleAddRow("excess_liability", currentRowIndex)}
              onRemoveRow={(index: number) => handleRemoveRow("excess_liability", index)}
              onRowChange={(key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("excess_liability", key, rowIndex, value)}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>OTHER</Typography>
            <CoverageOther
              rows={values.other}
              onAddRow={(currentRowIndex: number) => handleAddRow("other", currentRowIndex)}
              onRemoveRow={(index: number) => handleRemoveRow("other", index)}
              onRowChange={(key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("other", key, rowIndex, value)}
            />

            <Stack direction="row" spacing={3} sx={{ mt: 4 }}>
              <RoleBasedCustomButton
                moduleKey={MODULE_IDS.BUSINESS_POLICY}
                disabled={saveLoading}
                variant="contained"
                type="submit"
              >
                Save
              </RoleBasedCustomButton>
            </Stack>
          </FieldSet>
        </form>
      </StandadCard>
    </PageLoader>
    </PageContainer>
  );
};
